import React from 'react';
import SelectUi from '../../../ui/select.ui';
import {
  Timeframe,
  Compensation,
  ProjectRoles,
  ProjectStages,
  UserRole,
} from '../../../user/user.type';
import { OnboardingData } from '../../onboarding.type';
import InputUi, { InputType } from '../../../ui/input.ui';

import styles from './onboarding-step-role-preferences.module.css';

enum ProjectLabel {
  founderLabel = 'I\'m looking to meet a software engineer to...',
  engineerLabel = 'I\'m looking to join a startup that is...',
}

const createOptions = (enumValues: string[]) =>
  enumValues.map(value => ({ value, title: value }));

const projectRolesOptionsUi = createOptions(Object.values(ProjectRoles));
const projectStagesOptionsUi = createOptions(Object.values(ProjectStages));
const timeframeOptionsUi = createOptions(Object.values(Timeframe));
const compensationsOptionsUi = createOptions(Object.values(Compensation));

const LONG_DESCRIPTION_MIN_LENGTH = 100;
const LONG_DESCRIPTION_MAX_LENGTH = 10000;

type OnboardingStepRolePreferencesUiProps = {
  role: UserRole | '';
  projectRole?: string;
  projectStage?: string;
  timeframe?: Timeframe | null;
  compensation?: Compensation | null;
  longDescription?: string;
  projectRolesError: string | undefined;
  projectStageError: string | undefined;
  timeframeError: string | undefined;
  compensationError: string | undefined;
  longDescriptionError: string | undefined;
  isSubmitting?: boolean;
  isOpenToOtherRoles?: boolean;
  onUpdateField: (field: keyof OnboardingData, value: string | null) => void;
  onUpdateBooleanField: () => void;
}

const OnboardingStepRolePreferencesUi = (props: OnboardingStepRolePreferencesUiProps) => {
  const {
    role,
    projectRole,
    projectStage,
    timeframe,
    compensation,
    longDescription,
    projectRolesError,
    projectStageError,
    timeframeError,
    compensationError,
    longDescriptionError,
    isSubmitting,
    isOpenToOtherRoles,
    onUpdateField,
    onUpdateBooleanField,
  } = props;

  return (
    <div className={styles.layout}>
      {role === UserRole.SoftwareEngineer && (
        <InputUi
          type={InputType.checkbox}
          id="isOpenToOtherRoles"
          value={isOpenToOtherRoles ? 'true' : 'false'}
          labelText={'I\'m open to exploring other job openings at the Foundermatcha app.'}
          className={styles.checkboxLabel}
          style={{
            flexDirection: 'row-reverse',
            justifyContent: 'center',
          }}
          onChange={onUpdateBooleanField}
        />
      )}

      <div className={styles.formWrapper}>
        {role === UserRole.Founder ? (
          <>
            <SelectUi
              options={projectRolesOptionsUi}
              value={projectRole || ''}
              placeholder={ProjectRoles.ExploreFeasibility}
              labelText={ProjectLabel.founderLabel}
              disabled={isSubmitting}
              error={projectRolesError}
              onChange={(value) => onUpdateField('target_role', value)}
            />
          </>
        ) : (
          <SelectUi
            options={projectStagesOptionsUi}
            value={projectStage || ''}
            placeholder={ProjectStages.EarlyIdeation}
            labelText={ProjectLabel.engineerLabel}
            disabled={isSubmitting}
            error={projectStageError}
            onChange={(value) => onUpdateField('project_stage', value)}
          />
        )}

        <SelectUi
          id={'timeframe'}
          options={timeframeOptionsUi}
          value={timeframe || ''}
          placeholder={Timeframe.PartTime}
          labelText={'On a...'}
          disabled={isSubmitting}
          error={timeframeError}
          onChange={(value) => onUpdateField('timeframe', value)}
        />

        <SelectUi
          id={'compensation'}
          options={compensationsOptionsUi}
          value={compensation || ''}
          placeholder={Compensation.Equity}
          labelText={'For...'}
          disabled={isSubmitting}
          error={compensationError}
          onChange={(value) => onUpdateField('compensation', value)}
        />

        <div className={styles.longDescriptionWrapper}>
          <label className={styles.label}>
            {role === UserRole.SoftwareEngineer
              ? 'Here\'s the kind of developer I am and I am looking to join a startup that is...'
              : 'And it\'s important to me that the person is/has...'}
          </label>
          <textarea
            className={`${styles.textarea} ${longDescriptionError ? styles.textareaError : ''}`}
            value={longDescription || ''}
            onChange={(e) => onUpdateField('longDescription', e.target.value)}
            placeholder={
              role === UserRole.SoftwareEngineer
                ? 'Use this space to describe your technical skills, experience, and what you are looking for...'
                : 'Describe the ideal candidate, their values, experience, etc...'
            }
            disabled={isSubmitting}
            maxLength={LONG_DESCRIPTION_MAX_LENGTH}
          />
          <div
            className={styles.characterCounter}
            style={{
              color: (longDescription?.length || 0) >= LONG_DESCRIPTION_MIN_LENGTH ? 'green' : 'grey',
            }}
          >
            {longDescription?.length || 0}
            /{LONG_DESCRIPTION_MAX_LENGTH} characters (minimum: {LONG_DESCRIPTION_MIN_LENGTH})
          </div>
          {longDescriptionError && <div className={styles.error}>{longDescriptionError}</div>}
        </div>
      </div>
    </div>
  );
};

export default OnboardingStepRolePreferencesUi;
